import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, MessageCircle, Check } from "lucide-react";
import HeroScene from "@/components/3d/HeroScene";
import PackageFeatureCard from "@/components/showcase/PackageFeatureCard";
import Reveal from "@/components/ui/Reveal";
import FAQ from "@/components/ui/FAQ";
import OrderFlow from "@/components/ui/OrderFlow";
import { BOARDS, BRAND } from "@/constants/assets";
import {
  HERO,
  STATS,
  CORE_SERVICES,
  WORKFLOW,
  INDUSTRIES,
  wa,
} from "@/constants/content";
import usePageMeta from "@/hooks/usePageMeta";

const work = [
  {
    title: "Posters & social media creatives",
    tag: "₹349 - ₹1,499 / one-time",
    description:
      "The everyday offer lane for stories, posters, status drops and carousels that keep local brands visible.",
    highlights: ["Stories", "Offers", "Carousels"],
    image: BOARDS.postersSocial,
    to: "/services/posters-social",
    variant: "posters",
    layout: "hero",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    title: "Reels, branding & identity",
    tag: "₹999 - ₹12,999 / one-time",
    description:
      "Motion promos, logo systems and launch-ready brand assets grouped into one cleaner identity lane.",
    highlights: ["Reels", "Brand kits"],
    image: BOARDS.reelsBranding,
    to: "/services/reels-motion",
    variant: "identity",
    layout: "compact",
    span: "md:col-span-1",
  },
  {
    title: "Business profile, ads & campaigns",
    tag: "₹499 - ₹5,999 / one-time",
    description:
      "Profiles, ad creatives and paid campaign setup built for businesses moving from organic to performance.",
    highlights: ["Ads", "Profiles"],
    image: BOARDS.adsProfile,
    to: "/services/ads-profile",
    variant: "ads",
    layout: "compact",
    span: "md:col-span-1",
  },
  {
    title: "Universal monthly packages",
    tag: "₹4,999 - ₹24,999 / month",
    description:
      "Recurring content systems with planning, production and monthly momentum packaged into one subscription ladder.",
    highlights: ["Planning", "Growth", "Recurring output"],
    image: BOARDS.monthlyUniversal,
    to: "/services/monthly-systems",
    variant: "monthly",
    layout: "wide",
    span: "md:col-span-2",
  },
];

export default function Home() {
  usePageMeta({
    title: "Premium 3D Motion Graphics Studio",
    description:
      "ThemeStudios - Madurai-based digital design studio. Posters, reels, branding, ads and monthly creative systems for Indian small businesses. ₹349 to ₹29,999.",
    path: "/",
  });

  return (
    <div data-testid="page-home">
      <section className="relative min-h-[100svh] overflow-hidden">
        <HeroScene />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505] pointer-events-none" />

        <div className="relative z-10 pt-32 md:pt-36 px-6 md:px-12 max-w-[1440px] mx-auto">
          <Reveal>
            <p className="text-[10px] md:text-[11px] font-bold tracking-[0.32em] uppercase text-[#e03c31] mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-[#e03c31]" />
              {HERO.eyebrow}
            </p>
          </Reveal>

          <h1 className="h1-display text-white text-[8.5vw] md:text-[4.2vw] leading-[0.95] max-w-[20ch]">
            {HERO.headline1}
            <br />
            <span className="text-[#e03c31]">{HERO.headline2}</span>
            <br />
            {HERO.headline3} <span className="outline-text">{HERO.headline4}</span>
          </h1>

          <div className="mt-7 max-w-xl">
            <p className="text-sm md:text-[15px] text-zinc-300 leading-relaxed">{HERO.sub}</p>
          </div>

          <ul className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl">
            {HERO.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2.5 text-[13px] text-zinc-300">
                <Check size={14} className="text-[#e03c31] mt-1 shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#core-services"
              data-testid={HERO.primaryCta.testid}
              className="btn-magnetic"
            >
              {HERO.primaryCta.label} <ArrowUpRight size={14} />
            </a>
            <a
              href={wa("package")}
              target="_blank"
              rel="noreferrer"
              data-testid={HERO.secondaryCta.testid}
              className="btn-magnetic btn-ghost"
            >
              <MessageCircle size={14} /> {HERO.secondaryCta.label}
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-6 max-w-3xl">
            {STATS.map(({ k, v }) => (
              <div key={k}>
                <div className="font-cabinet text-2xl md:text-3xl font-bold tracking-tight">
                  {k}
                </div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em] mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 border-y border-white/5 overflow-hidden">
        <Marquee gradient={false} speed={45} pauseOnHover>
          <span className="marquee-text px-6 outline-text">Posters</span>
          <span className="marquee-text px-6 text-[#e03c31]">·</span>
          <span className="marquee-text px-6">Reels</span>
          <span className="marquee-text px-6 text-[#e03c31]">·</span>
          <span className="marquee-text px-6 outline-text">Branding</span>
          <span className="marquee-text px-6 text-[#e03c31]">·</span>
          <span className="marquee-text px-6">Ads</span>
          <span className="marquee-text px-6 text-[#e03c31]">·</span>
          <span className="marquee-text px-6 outline-text">Monthly Systems</span>
          <span className="marquee-text px-6 text-[#e03c31]">·</span>
        </Marquee>
      </section>

      <section
        id="core-services"
        className="px-6 md:px-12 py-20 md:py-28 max-w-[1440px] mx-auto scroll-mt-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12">
          <div className="md:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
              ✱ 5 core services + monthly systems
            </p>
            <h2 className="h2-section text-2xl md:text-4xl text-white">
              One studio, multiple service lanes, and a clearer 2026 pricing ladder.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-zinc-400 text-sm md:text-[15px] leading-relaxed">
              ThemeStudios helps business owners choose the format that matches how they actually
              market now - posters, stories, reels, branding kits, ad creatives, business profile
              support, or recurring monthly systems. Less confusion at the buying stage, cleaner
              scope, faster quoting.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
          {CORE_SERVICES.map((service, index) => (
            <Reveal
              key={service.n}
              delay={index * 60}
              className="tracing-border bg-[#0a0a0c] p-7 md:p-8 group cursor-pointer h-full card-glow"
            >
              <Link
                to={service.to}
                data-testid={`service-card-${index + 1}`}
                className="block h-full"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-cabinet text-[#e03c31] text-xs tracking-widest">
                    {service.n}
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="text-zinc-500 group-hover:text-[#e03c31] arrow-nudge"
                  />
                </div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2.5">
                  {service.count}
                </p>
                <h3 className="font-cabinet text-lg md:text-xl font-bold tracking-tight mb-3 leading-snug">
                  {service.title}
                </h3>
                <p className="text-zinc-400 text-[13px] leading-relaxed mb-5">{service.blurb}</p>
                <div className="pt-3 border-t border-white/5 text-xs font-bold text-white">
                  {service.price}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-12 md:py-16 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
              ✱ Real 2026 pricing boards
            </p>
            <h2 className="h2-section text-2xl md:text-4xl max-w-[20ch]">
              Live pricing pages - not placeholder art.
            </h2>
          </div>
          <a
            href={wa("package")}
            target="_blank"
            rel="noreferrer"
            className="btn-magnetic btn-ghost"
            data-testid="home-get-advice"
          >
            Get package advice <ArrowUpRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:auto-rows-[260px]">
          {work.map((item, index) => (
            <Reveal key={item.title} delay={index * 70} className={item.span}>
              <PackageFeatureCard
                to={item.to}
                title={item.title}
                tag={item.tag}
                description={item.description}
                highlights={item.highlights}
                image={item.image}
                variant={item.variant}
                layout={item.layout}
                testId={`home-work-${index + 1}`}
                className="h-full"
              />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-20 md:py-28 max-w-[1440px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
          ✱ Workflow
        </p>
        <h2 className="h2-section text-2xl md:text-4xl mb-12 max-w-[24ch]">
          Fast briefing, focused revisions, clean delivery.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {WORKFLOW.map((step, index) => (
            <Reveal key={step.n} delay={index * 70}>
              <div className="bg-[#0a0a0c] border border-white/5 rounded-2xl p-6 h-full card-lift card-glow">
                <span className="font-cabinet text-[#e03c31] text-xs tracking-widest">
                  {step.n}
                </span>
                <h3 className="font-cabinet text-base md:text-lg font-bold mt-2 mb-2.5 leading-snug">
                  {step.title}
                </h3>
                <p className="text-zinc-400 text-[13px] leading-relaxed">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-10 md:py-16 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
              ✱ Industry packs
            </p>
            <h2 className="h2-section text-2xl md:text-4xl max-w-[22ch]">
              Industry-specific monthly packs and branding kits.
            </h2>
          </div>
          <Link to="/services/monthly-systems" className="btn-magnetic btn-ghost">
            See monthly ladder <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INDUSTRIES.slice(0, 6).map((industry, index) => (
            <Reveal key={industry.name} delay={index * 50}>
              <div className="bg-[#0a0a0c] border border-white/5 rounded-2xl p-6 h-full card-lift card-glow">
                <h3 className="font-cabinet text-base md:text-lg font-bold leading-snug">
                  {industry.name}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#e03c31] font-bold mt-2 mb-3">
                  Typical range · {industry.range}
                </p>
                <p className="text-zinc-400 text-[13px] leading-relaxed">{industry.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-4">
          <Reveal>
            <div className="bg-[#0a0a0c] border border-white/5 rounded-2xl p-6 card-lift">
              <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
                <h3 className="font-cabinet text-base md:text-lg font-bold leading-snug">
                  {INDUSTRIES[6].name}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#e03c31] font-bold">
                  Typical range · {INDUSTRIES[6].range}
                </p>
              </div>
              <p className="text-zinc-400 text-[13px] leading-relaxed max-w-3xl">
                {INDUSTRIES[6].body}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <OrderFlow />

      <FAQ />

      <section className="px-6 md:px-12 py-20 md:py-28 max-w-[1440px] mx-auto">
        <div className="relative overflow-hidden rounded-[2rem] glass p-8 md:p-14 vignette">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
                ✱ Start a brief
              </p>
              <h2 className="h2-section text-3xl md:text-5xl leading-[0.98]">
                Need a cleaner quote for the full catalog?
              </h2>
              <p className="text-zinc-300 mt-5 max-w-xl text-sm md:text-[15px]">
                ThemeStudios has a dedicated brief form with grouped service selection, mobile-first
                enquiry, and a direct WhatsApp route.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link to="/contact" className="btn-magnetic" data-testid="home-open-contact">
                Open Contact Us page <ArrowUpRight size={14} />
              </Link>
              <a
                href={wa("brief")}
                target="_blank"
                rel="noreferrer"
                className="btn-magnetic btn-ghost"
                data-testid="home-whatsapp-cta"
              >
                <MessageCircle size={14} /> Talk on WhatsApp
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                className="btn-magnetic btn-ghost"
                data-testid="home-email-cta"
              >
                Email the studio
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
